import { styled } from 'styled-components';
import React, { useState, useEffect, FC, useCallback } from 'react';
import { DatePicker, Modal, Table } from 'antd';
import type { TableProps } from 'antd';
import { AppDispatch } from '@config/store';
import { useDispatch, useSelector } from 'react-redux';
import { PuffLoader } from 'react-spinners';
import { cssOverride, limitCardChanges, limitCardTrans } from '@utils/const';
import { rKeeperSelect } from '@entities/rKeeper';
import { flexMixin, textMixin } from '@styles/mixins';
import { ITransaction, logsSelect, setTableParams } from '@entities/logs';
import { Icons } from '@assets';
import { colors } from '@styles';
import { TransactionFilterModal } from './components/TransactionFilterModal';
import { authInfoSelect } from '@entities/auth';
import { getTransactions } from '@api/thunks/logs';
import { ItransactionReq } from '@api/services/logsApi/types';
import {
    getAllCardsSpendStats,
    getCardTemplates,
    getClientCardChanges,
    getClientCardTransactions,
    getClientCards,
} from '@api/thunks/card';
import FilterTransactionInfo from './components/FiltrTransactionInfo';
import { cardSelect, clearCardState } from '@entities/card';
import ClientModal from './components/ClientModal';
import { IPaginatedProps } from '@types';
import { getTransactionColumns } from './components/content/transactionColumns';
import TransactionModalCheck from './components/content/TransactionModalCheck';
import { getClient } from '@api/thunks/clients';
import { isNotEmptyArray } from '@utils/functions';
import dayjs from 'dayjs';

type DatePickerType = typeof DatePicker;
type DatePickerProps = React.ComponentProps<DatePickerType>;

const TransactionLogPage: FC = () => {
    const { transactions, tableParams, loading, totalTransactions } =
        useSelector(logsSelect);
    const { filters } = tableParams;
    const [openedFilterPopup, setOpenedFilterPopup] = useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();
    const [openedSettingsPopup, setOpenedSettingsPopup] =
        useState<boolean>(false);
    const { transactionCount, changesCount, templates } =
        useSelector(cardSelect);
    const [transfPagination, setTransfPagination] = useState<IPaginatedProps>({
        page: 1,
        limit: limitCardTrans,
    });
    const [changesPagination, setChangesPagination] = useState<IPaginatedProps>(
        {
            page: 1,
            limit: limitCardChanges,
        }
    );

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [selectedCheck, setSelectedCheck] = useState<ITransaction | null>(
        null
    );

    const { lastShiftClosed } = useSelector(rKeeperSelect);
    const { shop } = useSelector(authInfoSelect);

    const handleTableChange: TableProps<ITransaction>['onChange'] = (
        pagination,
        filters,
        sorter
    ) => {
        if (Array.isArray(sorter)) {
            sorter = sorter[0];
        }

        const newFilters = {
            ...tableParams.filters,
        };

        if (filters['client.sex']) {
            newFilters.sex = filters['client.sex'];
        }

        if (filters?.template && isNotEmptyArray(filters.template)) {
            newFilters.template = filters.template;
        }

        dispatch(
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    ...pagination,
                },
                filters: newFilters,
                field: sorter?.field,
                order: sorter?.order,
            })
        );
    };

    const handleShowClientModal = (isShow: boolean) => {
        setOpenedSettingsPopup(isShow);
        if (!isShow) {
            dispatch(clearCardState());
        }
    };

    useEffect(() => {
        if (shop && lastShiftClosed) {
            const { current, pageSize } = tableParams.pagination!;
            const { filters } = tableParams;

            const params: ItransactionReq = {
                limit: pageSize!,
                offset: (current! - 1) * pageSize!,
            };

            params.date = filters?.date
                ? filters.date.toString()
                : lastShiftClosed.SHIFTDATE;

            if (filters?.sex && filters.sex.length > 0) {
                params.sex = String(filters.sex[0]);
            }
            if (filters?.template) {
                params.template = filters.template.join(',');
            }
            if (filters?.payment) {
                params.payment = String(filters.payment[0]);
            }
            if (filters?.hours) {
                const time = filters.hours.toString().replace(':', ',');
                params.hours = time;
            }
            if (filters?.date) {
                params.date = filters.date.toString();
            }

            dispatch(getTransactions({ shop_id: shop.id, params }));
        }
    }, [tableParams, dispatch, shop, lastShiftClosed]);

    const handleOpenFilterModal = () => {
        setOpenedFilterPopup(true);
    };

    const handleCloseFilterModal = () => {
        setOpenedFilterPopup(false);
    };
    const loadMore = () => {
        if (transactionCount > transfPagination.page * transfPagination.limit) {
            const newPage = transfPagination.page + 1;
            setTransfPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };
    const handlerClickId = useCallback(
        (id: number, e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.preventDefault();

            if (shop) {
                const { limit, page } = transfPagination;
                const query = { limit, offset: (page - 1) * limit };
                dispatch(getClient({ shop_id: shop.id, client_id: id }));
                setOpenedSettingsPopup(true);
                const data = { client_id: id, shop_id: shop.id };

                dispatch(
                    getClientCardTransactions({
                        ...data,
                        query,
                    })
                );
                const { limit: limitChanges, page: pageChanges } =
                    changesPagination;
                const queryChanges = {
                    limit: limitChanges,
                    offset: (pageChanges - 1) * limitChanges,
                };

                dispatch(
                    getClientCardChanges({ ...data, query: queryChanges })
                );
                dispatch(getClientCards(data));
                dispatch(
                    getAllCardsSpendStats({
                        shop_id: shop.id,
                        client_id: id,
                    })
                );
            }
        },
        [dispatch, shop, transfPagination, changesPagination]
    );

    const loadMoreCardChanges = () => {
        if (changesCount > changesPagination.page * changesPagination.limit) {
            const newPage = changesPagination.page + 1;

            setChangesPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    const handleClickCheckId = (
        item: ITransaction,
        e: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => {
        setModalOpen(true);
        setSelectedCheck(item);
    };

    const handleDateChange: DatePickerProps['onChange'] = (date: unknown) => {
        const dayjsDate = date as dayjs.Dayjs | null;

        if (dayjsDate) {
            const apiFormatDate = dayjsDate.format('YYYY-MM-DD');
            dispatch(
                setTableParams({
                    ...tableParams,
                    pagination: {
                        ...tableParams.pagination,
                        current: 1,
                    },
                    filters: {
                        ...tableParams.filters,
                        date: dayjsDate ? (apiFormatDate as any) : null,
                    },
                })
            );
        }
    };

    const columns = getTransactionColumns(
        handlerClickId,
        handleClickCheckId,
        templates,
        expanded,
        setExpanded
    );

    useEffect(() => {
        if (shop) {
            dispatch(getCardTemplates(shop.id));
        }
    }, [dispatch, shop]);

    if (loading === 'loading') {
        return <PuffLoader color="#E65E20" cssOverride={cssOverride} />;
    }

    return (
        <Wrapper>
            <HeaderWrapper>
                <Title>Транзакции</Title>
                <InterfaceWrapper>
                    <StyledCalendarWrapper>
                        <DatePicker
                            onChange={handleDateChange}
                            format="DD-MM-YYYY"
                            value={
                                filters.date
                                    ? dayjs(filters.date as any, 'YYYY-MM-DD')
                                    : lastShiftClosed?.SHIFTDATE
                                      ? dayjs(
                                            lastShiftClosed.SHIFTDATE,
                                            'YYYY-MM-DD'
                                        )
                                      : null
                            }
                        />
                    </StyledCalendarWrapper>

                    <StyledButton onClick={handleOpenFilterModal}>
                        <IconWrapper>
                            <img
                                src={Icons.IconSearchVect2}
                                alt="icon search2"
                            />
                        </IconWrapper>
                    </StyledButton>
                </InterfaceWrapper>
            </HeaderWrapper>
            <FilterTransactionInfo
                total={totalTransactions}
                tableParams={tableParams}
            />
            <TableWrapper>
                <Table
                    size="small"
                    columns={columns}
                    rowKey={rec => rec.id}
                    dataSource={transactions}
                    onChange={handleTableChange}
                    pagination={{
                        current: tableParams.pagination?.current,
                        pageSize: tableParams.pagination?.pageSize,
                        total: totalTransactions,
                    }}
                />
            </TableWrapper>
            <Overlay
                display={
                    openedFilterPopup || openedSettingsPopup || modalOpen
                        ? 'block'
                        : 'none'
                }
            />

            {openedFilterPopup && (
                <TransactionFilterModal
                    modalVisibilityToggler={handleCloseFilterModal}
                />
            )}

            {openedSettingsPopup && (
                <ClientModal
                    loadMoreCardChanges={loadMoreCardChanges}
                    loadMore={loadMore}
                    setOpeningSettingsPopup={handleShowClientModal}
                />
            )}
            {modalOpen && (
                <StyledModal
                    centered
                    open={modalOpen}
                    onOk={() => setModalOpen(false)}
                    onCancel={() => setModalOpen(false)}
                    zIndex={3300}
                    width={1200}
                >
                    <TransactionModalCheck selectedCheck={selectedCheck} />
                </StyledModal>
            )}
        </Wrapper>
    );
};

export default TransactionLogPage;

const HeaderWrapper = styled.div`
    ${flexMixin('row', 'space-between', 'center')};
    padding: 20px 0px;
`;

const StyledCalendarWrapper = styled.div`
    ${flexMixin('row', 'space-between', 'center')};
    width: 100%;
    gap: 10px;
    color: ${colors.white};

    & svg {
        width: 20px;
        height: 20px;
    }
    .ant-picker {
        background-color: ${colors.primaryBg};
        font-family: Inter;
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;
        text-align: center;
        color: ${colors.white};
        height: 36px;
    }

    .ant-picker .ant-picker-input,
    .ant-picker .ant-picker-range,
    .ant-picker .ant-picker-input-active .ant-picker-input-placeholder {
        display: flex;
        flex-direction: row-reverse;
        ${textMixin({
            fontSize: '0.875rem',
            fontWeight: 400,
        })};
        line-height: 28px;
        color: ${colors.white};
    }

    .ant-picker .ant-picker-input .ant-picker-input-active {
        color: ${colors.white} !important;
    }

    .ant-picker .ant-picker-input .ant-picker-input-placeholder {
        color: ${colors.white}!important;
    }

    .ant-picker .ant-picker-input span.ant-picker-suffix,
    .ant-picker.ant-picker-range span.ant-picker-suffix {
        order: -1;
        margin-left: 0;
        margin-right: 10px;
        color: ${colors.white};
    }

    .ant-picker.ant-picker-range.ant-picker-range-separator {
        color: ${colors.white}!important;
    }

    .ant-picker-range-separator > span {
        color: ${colors.white}!important;
    }

    .ant-picker-clear {
        color: ${colors.white}!important;
    }

    .ant-picker .ant-picker-clear {
        position: absolute;
        right: 9px;
    }
`;

const TableWrapper = styled.div`
    margin-top: 10px;
    .ant-table-thead {
        height: 50px;
        line-height: 50px;
        margin-bottom: 5px !important;
        background: rgba(148, 129, 205, 0.25);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        border-radius: 5px 5px 0 0;
    }
    .ant-table-thead > tr > th::before {
        content: none !important;
    }
    .ant-table-cell {
        border-right: none !important;
        border-left: none !important;
    }

    .ant-table-row {
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
    }

    .ant-table-row .ant-table-cell {
        height: 46px !important;
        ${textMixin({
            fontSize: '0.875rem',
            fontWeight: 400,
        })}
        align-items: center;
        color: ${colors.grey};

        & svg {
            margin-top: 9px;
            cursor: pointer;
        }
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    min-height: calc(100vh - 110px);
    top: 0;
    background-color: ${colors.bgWhite};

    .ant-table-thead > tr > th {
        background-color: ${colors.tableHeadBg};
        line-height: 16px;
        text-align: left;
    }

    .ant-pagination {
        background-color: ${colors.tableHeadBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 16px;
        text-align: left;
        margin: 0 !important;
        margin-top: 5px !important;
        padding: 16px;
        background: ${colors.cleanWhite};
        box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1);
        border-radius: 0 0 5px 5px;
    }

    table > tbody > tr > td {
        background-color: ${colors.bgWhite};
        border: 1px solid #efefef;
        color: ${colors.grey};
        ${textMixin({
            fontSize: '0.875rem',
            fontWeight: 400,
        })};
        line-height: 16px;
        text-align: left;
        height: 30px;
    }
`;

const Title = styled.div`
    ${textMixin({
        fontSize: '2rem',
        fontWeight: 600,
    })};
    line-height: 28px;
    display: flex;
    align-items: center;
    color: ${colors.grey};
`;

const StyledButton = styled.button`
    width: 33px;
    height: 33px;
    background: #cd6ee5;
    box-sizing: border-box;
    border: 1px solid #cd6ee5;
    box-shadow: -1px 1px 4px 1px rgba(0, 0, 0, 0.24);
    border-radius: 3px;
    padding: 5px;
    cursor: pointer;
    margin-left: 10px;
`;
const Overlay = styled.div<{
    display: string;
}>`
    display: ${({ display }) => display};
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(1px);
`;
const StyledModal = styled(Modal)`
    z-index: 1500;
`;

const InterfaceWrapper = styled.div`
    ${flexMixin('row', 'center', 'center')};
`;
const IconWrapper = styled.div`
    ${flexMixin('column', 'center', 'center')}
    width: 24px;
    height: 24px;
`;
